import React, { useState } from 'react';
import { X, Send } from 'lucide-react';

const SubmitPluginModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: '', email: '', plugin: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setError(''); };

  const reset = () => {
    setForm({ name: '', email: '', plugin: '', description: '' });
    setSubmitted(false); setError('');
  };
  const close = () => { reset(); onClose(); };

  const submit = async (e) => {
    e.preventDefault();
    const email = form.email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError('Please enter a valid work email'); return; }
    if (!form.plugin.trim()) { setError('Give your plugin a name'); return; }
    setSubmitting(true); setError('');
    const API = process.env.REACT_APP_BACKEND_URL;
    try {
      await fetch(`${API}/api/events/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_type: 'plugin_submit',
          email,
          plugin: form.plugin.trim(),
          page: `${form.name.trim()} :: ${form.description.trim()}`.slice(0, 400),
        }),
      });
    } catch (_) { /* best-effort */ }
    setSubmitting(false); setSubmitted(true);
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
      onClick={close}
      data-testid="submit-plugin-modal"
    >
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl p-7" onClick={(e) => e.stopPropagation()}>
        <button onClick={close} className="absolute right-4 top-4 text-slate-400 hover:text-slate-900" data-testid="submit-plugin-close" aria-label="Close">
          <X className="w-5 h-5" />
        </button>

        {!submitted ? (
          <>
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-3">Build on Forsyt</div>
            <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight mb-2">Submit a plugin</h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-5">
              Tell us what you want to build on the Forsyt runtime. If it is a fit, we will get you a builder slot and review guidelines.
            </p>
            <form onSubmit={submit} className="space-y-3">
              <input value={form.name} onChange={set('name')} placeholder="Your name" data-testid="submit-plugin-name"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-400 transition-all" />
              <input type="email" value={form.email} onChange={set('email')} placeholder="name@company.com" data-testid="submit-plugin-email"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-400 transition-all" />
              <input value={form.plugin} onChange={set('plugin')} placeholder="Plugin name" data-testid="submit-plugin-pluginname"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-400 transition-all" />
              <textarea value={form.description} onChange={set('description')} placeholder="What does it do? What problem does it solve, and for whom?" rows={4} data-testid="submit-plugin-description"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-400 transition-all resize-none" />
              {error && <p className="text-red-500 text-xs" data-testid="submit-plugin-error">{error}</p>}
              <button type="submit" disabled={submitting} data-testid="submit-plugin-send"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 text-white text-sm font-semibold rounded-xl transition-all bg-slate-900 hover:bg-slate-800 disabled:opacity-60">
                {submitting ? 'Sending...' : (<>Submit plugin <Send className="w-4 h-4" /></>)}
              </button>
            </form>
          </>
        ) : (
          <div className="text-center py-4" data-testid="submit-plugin-done">
            <div className="mx-auto w-12 h-12 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-1">Got it.</h2>
            <p className="text-sm text-slate-500 leading-relaxed mb-5">Thanks for the submission. We review every plugin proposal and will be in touch about a builder slot.</p>
            <button onClick={close} className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all">Close</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubmitPluginModal;
